<script lang="ts">
  import { ethers } from '@credenza3/core-web-evm-ext'
  import { get } from 'svelte/store'

  import { clientPublicStore, fn, pageOptsStore, pageStore } from '@packages/stores'
  import { dispatch, once, PassportEvents } from '@lib/events/events'
  import { Pages } from '@packages/utils/enums'
  import { getTxScanAddress } from '@src/lib/tx/tx'
  import {
    CRED_SCANNER_TARGET_ID,
    createScanner,
    getScannerErrorMessage,
    setupScanner,
    type ScannerLib,
  } from '@lib/scanner'
  import { SendTokens} from '@packages/ui'
  import { getAddress } from '@src/passport/blockchain'
  import { configStore } from '@src/stores/config'
  import type { TEvmContract } from '@packages/utils/types'
  import { providerStore } from '@src/stores'
  import { getUserAddressBySub } from '@src/Passport.service'
  import { NOT_VALID_PASSPORT_QR_CODE_MESSAGE } from '@packages/utils/constants'

  const config = get(configStore)
  const { sendTokens, getCREDContract, toastAlert } = get(fn)

  let userAddress = ''
  let recipientAddress = ''
  let amount: number | undefined

  let activeCurrency = ''

  let decimals = 18
  let symbol = '-'
  let balance = ''

  let nativeDecimals = 18
  let nativeSymbol = '-'
  let nativeBalance = ''

  let isScannerHidden = true
  let isLoading = false

  let contract: ethers.Contract
  let signer: ethers.JsonRpcSigner
  let scanner: ScannerLib

  const onScannerClicked = async () => {
    const { scanner: scannerInstance, events } = await createScanner(CRED_SCANNER_TARGET_ID)
    scanner = scannerInstance
    once(PassportEvents.UI_CLOSED, scanner.close)
    setupScanner<{ sub: string }>(scanner, events, {
      onCapture: async ({ sub }) => {
        if (!sub) {
          toastAlert(NOT_VALID_PASSPORT_QR_CODE_MESSAGE(), 'failure')
          return scanner.close()
        }
        const { address } = await getUserAddressBySub(sub)
        recipientAddress = address
        scanner.close()
      },
      onCancel: () => (isScannerHidden = true),
      onError: (err) => {
        const message = getScannerErrorMessage(err)
        if (message?.includes('Permission'))
          alert('In order to scan please allow the current page to access your camera and try again.')
        isScannerHidden = true
      },
    })
    isScannerHidden = false
    await scanner.scan()
  }

  const sendTokensToRecipient = async () => {
    const isTCRED = activeCurrency === symbol
    isLoading = true
    try {
      const weiBn = ethers.parseUnits(String(amount), isTCRED ? decimals : nativeDecimals)
      const result = (await sendTokens({
        address: recipientAddress,
        amount: weiBn,
        instance: isTCRED ? contract : undefined,
      })) as { hash: string; wait: () => Promise<void> }
      toastAlert(
        `<a href="${getTxScanAddress(result.hash)}" target="_blank">Send TX</a> was created`,
      )
      recipientAddress = ''
      amount = undefined

      const [weiBalance, weiNativeBalance] = await Promise.all([
        contract.balanceOf(userAddress),
        signer.provider.getBalance(userAddress),
      ])

      balance = ethers.formatUnits(weiBalance, decimals)
      nativeBalance = parseFloat(ethers.formatUnits(weiNativeBalance, nativeDecimals)).toFixed(4)
      dispatch(PassportEvents.RECHECK_BALANCE, [result.hash])
    } catch (err) {
      toastAlert(err?.message || err || 'An error occurred while sending tokens', 'failure')
      dispatch(PassportEvents.ERROR, { error: err })
    }
    isLoading = false
  }

  const init = async () => {
    const pageOpts = get(pageOptsStore)
    recipientAddress = pageOpts?.to || ''
    amount = pageOpts?.amount || undefined
    const { decimals: contractDecimals, contract: credContract } = (await getCREDContract()) as TEvmContract
    decimals = contractDecimals
    contract = credContract
    const provider = get(providerStore)
    if(!provider) return
    signer = await provider.getSigner()
    userAddress = await getAddress()
    const results = await Promise.all([
      contract.symbol(),
      contract.balanceOf(userAddress),
      provider.getBalance(userAddress),
      fetch('https://chainid.network/chains.json')
        .then((result) => result.json())
        .then((networks) =>
          networks.find((network: { chainId: number }) => String(network.chainId) === config.chainId),
        ),
    ])
    symbol = results[0]
    balance = ethers.formatUnits(results[1], decimals)
    nativeDecimals = results[3]?.nativeCurrency?.decimals ?? 18
    nativeBalance = parseFloat(ethers.formatUnits(results[2], nativeDecimals)).toFixed(4)
    nativeSymbol = results[3]?.nativeCurrency?.symbol ?? 'ETH'
    activeCurrency = symbol
  }

  $: $configStore.chainId && $providerStore && init()
  $: isRecipientAddressValid = !!ethers.isAddress(recipientAddress)
  $: isSubmitDisabled =
    isLoading ||
    !amount ||
    !isRecipientAddressValid ||
    (activeCurrency === symbol && amount > +balance) ||
    (activeCurrency === nativeSymbol && amount > +nativeBalance)
</script>

<SendTokens
  bind:recipientAddress
  bind:amount
  options={{
    title: `Send stored value`,
    isNativeCurrecyTransferAllowed: $configStore.transferTokens?.nativeCurrency !== false,
    isScannerHidden,
    balance,
    chainId: config.chainId,
    isRecipientAddressValid,
    onScannerClicked,
    onSendTokens: sendTokensToRecipient,
    onBackClicked: () => pageStore.set(Pages.WALLET),
    onChangeCurrency: () => (activeCurrency === symbol ? (activeCurrency = nativeSymbol) : (activeCurrency = symbol)),
    onSetMaxAmount: () => (activeCurrency === symbol ? (amount = +balance) : (amount = +nativeBalance)),
    isLoading,
    isSubmitDisabled,
    credAlias: $clientPublicStore?.ui_settings.cred_alias,
    activeCurrency,
    symbol,
    nativeSymbol,
    nativeBalance,
  }}
/>
